import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ArrowLeft, Loader2, CheckCircle2, Upload, Lightbulb, Volume2,
  Play, Pause, Printer, Download, Info, CircleCheck, Circle,
  ChevronDown,
  BookOpen, PartyPopper, ClipboardCheck
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { StepChecklist, OutcomeChecklist, YouTubeReferences, ParentPrepVideos, CurricularAlignment } from "@/components/ActivityComponents";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Progress bar component
const ProgressBar = ({ completed, total, label }) => {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div data-testid="progress-bar" className="w-full">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-foreground/70">{label}</span>
        <span className="text-sm font-bold text-primary">{completed}/{total} ({pct}%)</span>
      </div>
      <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${pct}%`,
            background: pct === 100
              ? 'linear-gradient(90deg, #22c55e, #16a34a)'
              : 'linear-gradient(90deg, #f97316, #ea580c)'
          }}
        />
      </div>
    </div>
  );
};

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState(null);
  const [activeTab, setActiveTab] = useState("activity");
  const [artifacts, setArtifacts] = useState([]);

  // Step completion tracking (localStorage)
  const [completedSteps, setCompletedSteps] = useState({});

  // Outcome achievement tracking (localStorage)
  const [achievedOutcomes, setAchievedOutcomes] = useState({});

  // Expanded parent tips
  const [expandedTips, setExpandedTips] = useState({});

  // Enhanced Feedback form
  const [feedbackForm, setFeedbackForm] = useState({
    experience: "",
    additional_comments: "",
    completion_status: "",
    likert_rating: 0,
    outcome_assessments: {}
  });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Artifact upload
  const [uploadingArtifact, setUploadingArtifact] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [artifactTitle, setArtifactTitle] = useState("");
  const [includeInPortfolio, setIncludeInPortfolio] = useState(false);

  // Audio state
  const [audioData, setAudioData] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Gate server writes until initial progress load completes
  const [progressLoaded, setProgressLoaded] = useState(false);

  // Load progress (server when authenticated, localStorage otherwise)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const loadLocal = () => {
      const savedSteps = localStorage.getItem(`revivedu_steps_${id}`);
      const savedOutcomes = localStorage.getItem(`revivedu_outcomes_${id}`);
      if (savedSteps) setCompletedSteps(JSON.parse(savedSteps));
      if (savedOutcomes) setAchievedOutcomes(JSON.parse(savedOutcomes));
    };

    const loadProgress = async () => {
      if (isAuthenticated) {
        try {
          const res = await axios.get(`${API}/activities/${id}/progress`, { headers: getAuthHeaders() });
          if (cancelled) return;
          const steps = res.data?.completed_steps || {};
          const outcomes = res.data?.achieved_outcomes || {};
          if (Object.keys(steps).length || Object.keys(outcomes).length) {
            setCompletedSteps(steps);
            setAchievedOutcomes(outcomes);
          } else {
            loadLocal();
          }
        } catch (e) {
          loadLocal();
        }
      } else {
        loadLocal();
      }
      if (!cancelled) setProgressLoaded(true);
    };

    loadProgress();
    return () => { cancelled = true; };
  }, [id, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist progress (localStorage always + server when authenticated)
  useEffect(() => {
    if (!id || !progressLoaded) return;
    localStorage.setItem(`revivedu_steps_${id}`, JSON.stringify(completedSteps));
    localStorage.setItem(`revivedu_outcomes_${id}`, JSON.stringify(achievedOutcomes));
    if (isAuthenticated) {
      axios.put(
        `${API}/activities/${id}/progress`,
        { completed_steps: completedSteps, achieved_outcomes: achievedOutcomes },
        { headers: getAuthHeaders() }
      ).catch(() => {});
    }
  }, [completedSteps, achievedOutcomes, id, progressLoaded, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchActivity();
    fetchArtifacts();
  }, [id]);

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API}/activities/${id}`);
      setActivity(response.data);
    } catch (error) {
      console.error("Error fetching activity:", error);
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  const fetchArtifacts = async () => {
    try {
      const response = await axios.get(`${API}/artifacts/${id}`);
      setArtifacts(response.data);
    } catch (error) {
      console.error("Error fetching artifacts:", error);
    }
  };

  const toggleStep = useCallback((index) => {
    setCompletedSteps(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);

  const toggleOutcome = useCallback((outcome) => {
    setAchievedOutcomes(prev => ({ ...prev, [outcome]: !prev[outcome] }));
  }, []);

  const toggleTip = useCallback((index) => {
    setExpandedTips(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);

  const getStepProgress = () => {
    if (!activity?.instructions) return { completed: 0, total: 0 };
    const total = activity.instructions.length;
    const completed = Object.values(completedSteps).filter(Boolean).length;
    return { completed, total };
  };

  const getOutcomeProgress = () => {
    const outcomes = activity?.outcome_analysis || [];
    if (outcomes.length === 0 && activity?.learning_outcomes) {
      const total = activity.learning_outcomes.length;
      const completed = Object.values(achievedOutcomes).filter(Boolean).length;
      return { completed, total };
    }
    const total = outcomes.length;
    const completed = outcomes.filter(item => achievedOutcomes[item.outcome]).length;
    return { completed, total };
  };

  const generateAudio = async () => {
    setLoadingAudio(true);
    try {
      const response = await axios.get(`${API}/activities/${id}/audio`);
      setAudioData(response.data);
      toast.success("Audio summary generated!");
    } catch (error) {
      console.error("Error generating audio:", error);
      toast.error("Failed to generate audio summary");
    } finally {
      setLoadingAudio(false);
    }
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); } else { audio.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) { audioRef.current.playbackRate = speed; }
  };

  const handlePrint = () => { window.print(); };

  const handleWhatsAppShare = () => {
    const shareUrl = window.location.href;
    const text = `Check out this learning activity on Revivedu: "${activity.title}"\n\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const escapeHtml = (str) => {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const handleDownloadPDF = () => {
    const htmlContent = `
      <!DOCTYPE html><html><head><title>${escapeHtml(activity.title)} - Revivedu</title>
      <style>
        body { font-family: system-ui, -apple-system, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        h1 { color: #1a365d; margin-bottom: 20px; }
        h2 { color: #2d3748; margin-top: 30px; margin-bottom: 15px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; }
        p { line-height: 1.6; color: #4a5568; }
        ul, ol { margin-left: 20px; line-height: 1.8; }
        li { margin-bottom: 8px; }
        .tag { display: inline-block; background: #edf2f7; padding: 4px 12px; border-radius: 20px; margin: 4px; font-size: 14px; }
        .section { margin-bottom: 30px; }
        .objective-box { background: #f7fafc; border-left: 4px solid #3182ce; padding: 15px; margin: 15px 0; }
        .parent-guide { background: #fefce8; border-left: 4px solid #f59e0b; padding: 15px; margin: 15px 0; border-radius: 8px; }
        .parent-tip { background: #eff6ff; padding: 10px; margin: 5px 0; border-radius: 6px; font-size: 13px; color: #1e40af; }
        .header-info { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 20px; }
        @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
      </style></head><body>
        <h1>${escapeHtml(activity.title)}</h1>
        <div class="header-info">
          <span class="tag">Age: ${escapeHtml(activity.age)}</span>
          ${activity.estimated_time ? `<span class="tag">Duration: ${escapeHtml(activity.estimated_time)}</span>` : ''}
          ${activity.difficulty ? `<span class="tag">Difficulty: ${escapeHtml(activity.difficulty)}</span>` : ''}
        </div>
        ${activity.parent_guide ? `<div class="parent-guide"><strong>Guide for Parents:</strong><br/>${escapeHtml(activity.parent_guide)}</div>` : ''}
        ${activity.objective ? `<div class="section"><h2>What This Activity Is About</h2><div class="objective-box">${escapeHtml(activity.objective)}</div></div>` : ''}
        <div class="section"><h2>Overview</h2><p>${escapeHtml(activity.description)}</p></div>
        ${activity.expected_outcome ? `<div class="section"><h2>What Your Child Will Gain</h2><p>${escapeHtml(activity.expected_outcome)}</p></div>` : ''}
        ${activity.materials_required?.length > 0 ? `<div class="section"><h2>What You'll Need</h2><ul>${activity.materials_required.map(m => `<li>${escapeHtml(m)}</li>`).join('')}</ul></div>` : ''}
        <div class="section"><h2>Activity Steps</h2><ol>${activity.instructions.map((inst, i) => `<li>${escapeHtml(inst)}${activity.parent_tips?.[i] ? `<div class="parent-tip"><strong>Parent Tip:</strong> ${escapeHtml(activity.parent_tips[i])}</div>` : ''}</li>`).join('')}</ol></div>
        ${activity.success_metrics?.length > 0 ? `<div class="section"><h2>How to Know It's Working</h2><ul>${activity.success_metrics.map(m => `<li>${escapeHtml(m)}</li>`).join('')}</ul></div>` : ''}
        ${activity.reflection_question ? `<div class="section"><h2>Ask Your Child</h2><p><em>"${escapeHtml(activity.reflection_question)}"</em></p></div>` : ''}
        ${activity.learning_outcomes?.length > 0 ? `<div class="section"><h2>What Your Child Will Learn</h2><ul>${activity.learning_outcomes.map(o => `<li>${escapeHtml(o)}</li>`).join('')}</ul></div>` : ''}
        ${activity.real_world_connection ? `<div class="section"><h2>Real-Life Connection</h2><p>${escapeHtml(activity.real_world_connection)}</p></div>` : ''}
        <footer style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; color: #718096; font-size: 12px;">Generated by Revivedu - Reviving the joy of learning</footer>
      </body></html>
    `;
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      };
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (feedbackForm.likert_rating === 0) { toast.error("Please provide an activity rating (1-5)"); return; }
    if (!feedbackForm.completion_status) { toast.error("Please select a completion status"); return; }
    if (!feedbackForm.experience) { toast.error("Please describe the experience"); return; }

    const outcomesAchieved = Object.entries(feedbackForm.outcome_assessments)
      .filter(([_, data]) => data.achieved)
      .map(([outcome]) => outcome);

    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/feedback`, {
        activity_id: id,
        child_id: activity?.child_id || null,
        rating: feedbackForm.likert_rating,
        experience: feedbackForm.experience,
        outcomes: outcomesAchieved.join(", "),
        suggestions: feedbackForm.additional_comments,
        completion_status: feedbackForm.completion_status,
        outcomes_achieved: outcomesAchieved,
        outcome_assessments: feedbackForm.outcome_assessments
      });
      toast.success("Feedback submitted successfully!");
      setFeedbackForm({ experience: "", additional_comments: "", completion_status: "", likert_rating: 0, outcome_assessments: {} });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) { toast.error("Please select a file"); return; }

    setUploadingArtifact(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("activity_id", id);
      formData.append("title", artifactTitle);
      formData.append("include_in_portfolio", includeInPortfolio.toString());
      if (activity?.child_id) { formData.append("child_id", activity.child_id); }

      const headers = isAuthenticated
        ? { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
        : { "Content-Type": "multipart/form-data" };

      await axios.post(`${API}/artifacts`, formData, { headers });
      toast.success("Artefact uploaded successfully!");
      setSelectedFile(null);
      setArtifactTitle("");
      setIncludeInPortfolio(false);
      fetchArtifacts();
    } catch (error) {
      console.error("Error uploading artifact:", error);
      toast.error("Failed to upload artefact");
    } finally {
      setUploadingArtifact(false);
    }
  };

  const handleOutcomeAssessment = (outcome, field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      outcome_assessments: {
        ...prev.outcome_assessments,
        [outcome]: { ...prev.outcome_assessments[outcome], [field]: value }
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-foreground/60 mb-4">Activity not found</p>
          <Button onClick={() => navigate("/library")} className="rounded-full">Go to Library</Button>
        </div>
      </div>
    );
  }

  const stepProgress = getStepProgress();
  const outcomeProgress = getOutcomeProgress();
  const allStepsDone = stepProgress.completed === stepProgress.total && stepProgress.total > 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-6 py-12" id="activity-content">
        <Button data-testid="back-library-btn" variant="ghost" onClick={() => navigate("/library")} className="mb-6 rounded-full print:hidden">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Library
        </Button>

        {/* Header */}
        <div className="mb-8" data-testid="activity-header">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4 gap-4">
            <div className="flex-1">
              <h1 className="text-4xl sm:text-5xl font-bold text-secondary">{activity.title}</h1>
            </div>
            <div className="flex flex-wrap gap-2 print:hidden w-full md:w-auto">
              <Button data-testid="whatsapp-share-btn" onClick={handleWhatsAppShare} variant="outline" className="rounded-full border-2 border-green-500 text-green-600 hover:bg-green-500 hover:text-white">
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Share
              </Button>
              <Button data-testid="print-btn" onClick={handlePrint} variant="outline" className="rounded-full border-2">
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
              <Button data-testid="download-pdf-btn" onClick={handleDownloadPDF} variant="outline" className="rounded-full border-2">
                <Download className="mr-2 h-4 w-4" /> Download PDF
              </Button>
              <Button data-testid="generate-audio-btn" onClick={generateAudio} disabled={loadingAudio} variant="outline" className="rounded-full border-2 border-accent text-accent hover:bg-accent hover:text-white">
                {loadingAudio ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>) : (<><Volume2 className="mr-2 h-4 w-4" /> Listen</>)}
              </Button>
            </div>
          </div>

          {/* Audio Player */}
          {audioData && (
            <Card className="rounded-3xl border-accent/50 bg-accent/5 shadow-sm mb-4 print:hidden" data-testid="audio-player">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Button data-testid="play-pause-btn" onClick={handlePlayPause} size="lg" className="rounded-full bg-accent hover:bg-accent/90">
                    {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                  </Button>
                  <div className="flex-1">
                    <p className="text-sm text-foreground/60 mb-2">Activity Audio Summary</p>
                    <audio ref={audioRef} src={`data:audio/mp3;base64,${audioData.audio_base64}`} onEnded={() => setIsPlaying(false)} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} className="w-full" controls />
                  </div>
                  <div className="flex gap-2">
                    <p className="text-sm text-foreground/60 mr-2">Speed:</p>
                    {[1.0, 1.5, 2.0].map((speed) => (
                      <Button key={speed} data-testid={`speed-${speed}x`} onClick={() => handleSpeedChange(speed)} variant={playbackSpeed === speed ? "default" : "outline"} size="sm" className="rounded-full">{speed}x</Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Parent Guide */}
          {activity.parent_guide && (
            <Card className="rounded-3xl border-amber-200 bg-amber-50 shadow-sm mb-4" data-testid="parent-guide-card">
              <CardContent className="pt-6 pb-5">
                <div className="flex gap-3">
                  <BookOpen className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-amber-800 mb-2 text-lg">Guide for Parents</h3>
                    <p className="text-amber-900/80 leading-relaxed">{activity.parent_guide}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activity.objective && (
            <div className="bg-accent/10 border-l-4 border-accent p-4 rounded-lg mb-4">
              <h3 className="font-bold text-accent-foreground mb-2">What This Activity Is About</h3>
              <p className="text-foreground/80">{activity.objective}</p>
            </div>
          )}

          <p className="text-lg text-foreground/80 mb-4">{activity.description}</p>

          {activity.expected_outcome && (
            <div className="bg-primary/10 border-l-4 border-primary p-4 rounded-lg mb-4">
              <h3 className="font-bold text-primary mb-2">What Your Child Will Gain</h3>
              <p className="text-foreground/80">{activity.expected_outcome}</p>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-4 py-2 bg-accent/20 text-accent-foreground rounded-full text-sm font-semibold">Age: {activity.age}</span>
            {activity.difficulty && (
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${activity.difficulty === 'easy' ? 'bg-green-100 text-green-700' : activity.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                {activity.difficulty.charAt(0).toUpperCase() + activity.difficulty.slice(1)}
              </span>
            )}
            {activity.estimated_time && (
              <span className="px-4 py-2 bg-secondary/20 text-secondary rounded-full text-sm font-semibold">Duration: {activity.estimated_time}</span>
            )}
            {activity.subjects.map((subject) => (
              <span key={subject} className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm">{subject}</span>
            ))}
          </div>

          {/* Overall Progress Summary */}
          {(stepProgress.total > 0 || outcomeProgress.total > 0) && (
            <Card className="rounded-3xl border-border/50 shadow-sm mb-0 print:hidden" data-testid="progress-summary-card">
              <CardContent className="pt-6 pb-5">
                <div className="grid sm:grid-cols-2 gap-6">
                  {stepProgress.total > 0 && (
                    <ProgressBar completed={stepProgress.completed} total={stepProgress.total} label="Steps Completed" />
                  )}
                  {outcomeProgress.total > 0 && (
                    <ProgressBar completed={outcomeProgress.completed} total={outcomeProgress.total} label="Outcomes Achieved" />
                  )}
                </div>
                {allStepsDone && (
                  <div className="mt-4 flex items-center gap-2 text-green-600 bg-green-50 rounded-xl px-4 py-3">
                    <PartyPopper className="h-5 w-5" />
                    <span className="font-semibold">All steps completed! Great job!</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full print:hidden">
          <TabsList className="grid w-full grid-cols-3 rounded-full p-1 bg-muted" data-testid="activity-tabs">
            <TabsTrigger value="activity" className="rounded-full" data-testid="tab-activity">Activity</TabsTrigger>
            <TabsTrigger value="progress" className="rounded-full" data-testid="tab-progress">
              <ClipboardCheck className="mr-1.5 h-4 w-4" /> Track Progress
            </TabsTrigger>
            <TabsTrigger value="artifacts" className="rounded-full" data-testid="tab-artifacts">Upload ({artifacts.length})</TabsTrigger>
          </TabsList>

          {/* =================== ACTIVITY TAB =================== */}
          <TabsContent value="activity" className="mt-8">
            <div className="space-y-8">
              {/* Materials */}
              {activity.materials_required?.length > 0 && (
                <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="materials-card">
                  <CardHeader>
                    <CardTitle className="text-2xl text-secondary">What You'll Need</CardTitle>
                    <CardDescription>Gather these items before starting the activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="grid sm:grid-cols-2 gap-3">
                      {activity.materials_required.map((material, index) => (
                        <li key={index} className="flex gap-2 items-start">
                          <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                          <span className="text-foreground/80">{material}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Parent Prep videos (Section 21) - between materials and timed steps */}
              <ParentPrepVideos videos={activity.parent_prep_videos} />

              {/* Step-by-Step Checklist */}
              <StepChecklist
                instructions={activity.instructions}
                parentTips={activity.parent_tips}
                completedSteps={completedSteps}
                expandedTips={expandedTips}
                onToggleStep={toggleStep}
                onToggleTip={toggleTip}
              />

              {/* How to Know It's Working */}
              {activity.success_metrics?.length > 0 && (
                <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="success-metrics-card">
                  <CardHeader>
                    <CardTitle className="text-2xl text-secondary">How to Know It's Working</CardTitle>
                    <CardDescription>Look for these signs while your child is doing the activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {activity.success_metrics.map((metric) => (
                        <li key={metric} className="flex gap-3">
                          <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0" />
                          <span className="text-foreground/80">{metric}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* YouTube References for Parents */}
              <YouTubeReferences references={activity.youtube_references} />

              {/* Ask Your Child */}
              {activity.reflection_question && (
                <Card className="rounded-3xl border-border/50 shadow-sm bg-gradient-to-r from-accent/5 to-primary/5" data-testid="reflection-card">
                  <CardHeader>
                    <CardTitle className="text-2xl text-secondary">Ask Your Child</CardTitle>
                    <CardDescription>After the activity, ask this question to encourage deeper thinking</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg text-foreground/80 italic">"{activity.reflection_question}"</p>
                  </CardContent>
                </Card>
              )}

              {/* What Your Child Will Learn */}
              {activity.learning_outcomes?.length > 0 && (
                <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="outcomes-card">
                  <CardHeader>
                    <CardTitle className="text-2xl text-secondary">What Your Child Will Learn</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {activity.learning_outcomes.map((outcome, index) => (
                        <li key={index} className="flex gap-3">
                          <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0" />
                          <span className="text-foreground/80">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Skills Your Child Will Practice */}
              {activity.skills?.length > 0 && (
                <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="skills-card">
                  <CardHeader>
                    <CardTitle className="text-2xl text-secondary">Skills Your Child Will Practice</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <TooltipProvider>
                      <div className="flex flex-wrap gap-3">
                        {activity.skills.map((skill) => (
                          <div key={skill}>
                            <span className="px-6 py-3 bg-secondary/10 text-secondary rounded-full font-semibold inline-flex items-center gap-2">
                              {skill}
                              {activity.skill_explanations?.[skill] && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button type="button" className="text-secondary/60 hover:text-secondary"><Info className="h-4 w-4" /></button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="font-semibold mb-1">How this skill is developed:</p>
                                    <p className="text-sm">{activity.skill_explanations[skill]}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </TooltipProvider>
                  </CardContent>
                </Card>
              )}

              {/* Extensions */}
              {activity.extensions?.length > 0 && (
                <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="extensions-card">
                  <CardHeader>
                    <CardTitle className="text-2xl text-secondary">Want to Do More?</CardTitle>
                    <CardDescription>Try these variations to extend the learning</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {activity.extensions.map((extension) => (
                        <li key={extension} className="flex gap-3"><Lightbulb className="h-6 w-6 text-accent flex-shrink-0" /><span className="text-foreground/80">{extension}</span></li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Discussion Questions */}
              {activity.discussion_questions?.length > 0 && (
                <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="discussion-card">
                  <CardHeader>
                    <CardTitle className="text-2xl text-secondary">Talk About It</CardTitle>
                    <CardDescription>Use these questions to have a conversation with your child</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {activity.discussion_questions.map((question, index) => (
                        <li key={`q-${question.slice(0, 20)}`} className="text-foreground/80"><span className="font-semibold text-primary">Q{index + 1}:</span> {question}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Real-Life Connection */}
              {activity.real_world_connection && (
                <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="real-world-card">
                  <CardHeader>
                    <CardTitle className="text-2xl text-secondary">Real-Life Connection</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/80">{activity.real_world_connection}</p>
                  </CardContent>
                </Card>
              )}

              {/* Curricular Alignment (Section 20) - parent-friendly, hidden if empty */}
              <CurricularAlignment
                alignment={activity.parent_alignment}
                curricularAreas={activity.curricular_areas}
                learningOutcomes={activity.learning_outcomes}
              />
            </div>
          </TabsContent>

          {/* =================== TRACK PROGRESS TAB =================== */}
          <TabsContent value="progress" className="mt-8">
            <div className="space-y-8">
              {/* Outcome Achievement Checklist */}
              <OutcomeChecklist
                outcomeAnalysis={activity.outcome_analysis}
                learningOutcomes={activity.learning_outcomes}
                achievedOutcomes={achievedOutcomes}
                onToggleOutcome={toggleOutcome}
              />

              {outcomeProgress.completed === outcomeProgress.total && outcomeProgress.total > 0 && (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-xl px-4 py-3">
                  <PartyPopper className="h-5 w-5" />
                  <span className="font-semibold">All outcomes achieved! Your child is doing wonderfully!</span>
                </div>
              )}

              {/* Feedback Form */}
              <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="feedback-form-card">
                <CardHeader>
                  <CardTitle className="text-2xl text-secondary">Share Your Experience</CardTitle>
                  <CardDescription>Tell us how the activity went so we can improve</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleFeedbackSubmit} className="space-y-8">
                    {/* Completion Status */}
                    <div>
                      <Label className="text-base mb-3 block font-semibold">How far did you get? *</Label>
                      <Select value={feedbackForm.completion_status} onValueChange={(value) => setFeedbackForm({ ...feedbackForm, completion_status: value })}>
                        <SelectTrigger data-testid="completion-status-select" className="h-12 rounded-xl border-2">
                          <SelectValue placeholder="Select completion status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="completed">Completed the full activity</SelectItem>
                          <SelectItem value="partially_completed">Completed some parts</SelectItem>
                          <SelectItem value="not_engaged">Did not engage with the activity</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Likert Scale Rating */}
                    <div>
                      <Label className="text-base mb-3 block font-semibold">How helpful was this activity? (1-5) *</Label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((num) => (
                          <button key={num} type="button" data-testid={`likert-rating-${num}`} onClick={() => setFeedbackForm({ ...feedbackForm, likert_rating: num })}
                            className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold transition-all ${feedbackForm.likert_rating === num ? 'bg-primary text-white border-primary' : 'border-border hover:border-primary'}`}>
                            {num}
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-foreground/60"><span>Not helpful</span><span>Very helpful</span></div>
                    </div>

                    {/* Outcome Assessments for feedback */}
                    {activity.outcome_analysis?.length > 0 && (
                      <div>
                        <Label className="text-base mb-3 block font-semibold">Learning Outcomes Assessment</Label>
                        <p className="text-sm text-foreground/60 mb-4">Mark which outcomes your child achieved during this activity</p>
                        <div className="space-y-4">
                          {activity.outcome_analysis.map((item, index) => {
                            const assessment = feedbackForm.outcome_assessments[item.outcome] || { achieved: false, notes: '' };
                            return (
                              <div key={index} className="bg-muted/50 rounded-2xl p-4 border border-border/50">
                                <div className="flex items-start justify-between gap-4 mb-2">
                                  <h4 className="font-semibold text-secondary flex-1">{item.outcome}</h4>
                                  <button type="button" onClick={() => handleOutcomeAssessment(item.outcome, 'achieved', !assessment.achieved)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all ${assessment.achieved ? 'bg-green-100 border-green-500 text-green-700' : 'bg-muted border-border hover:border-primary'}`}
                                    data-testid={`outcome-toggle-${index}`}>
                                    {assessment.achieved ? (<><CircleCheck className="h-5 w-5" /><span className="font-medium">Achieved</span></>) : (<><Circle className="h-5 w-5" /><span className="font-medium">Not Yet</span></>)}
                                  </button>
                                </div>
                                <div>
                                  <Label className="text-xs text-foreground/60 mb-1 block">Notes (optional)</Label>
                                  <Input placeholder="Any observations about this outcome..." value={assessment.notes || ''} onChange={(e) => handleOutcomeAssessment(item.outcome, 'notes', e.target.value)} className="h-9 text-sm rounded-lg" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="experience" className="text-base mb-2 block font-semibold">How did it go? *</Label>
                      <Textarea id="experience" data-testid="feedback-experience" placeholder="What worked well? What was challenging?" value={feedbackForm.experience} onChange={(e) => setFeedbackForm({ ...feedbackForm, experience: e.target.value })} className="min-h-[100px] rounded-xl border-2" required />
                    </div>

                    <div>
                      <Label htmlFor="additional_comments" className="text-base mb-2 block">Any other thoughts or suggestions?</Label>
                      <Textarea id="additional_comments" data-testid="feedback-additional-comments" placeholder="What could make this activity better?" value={feedbackForm.additional_comments} onChange={(e) => setFeedbackForm({ ...feedbackForm, additional_comments: e.target.value })} className="min-h-[100px] rounded-xl border-2" />
                    </div>

                    <Button data-testid="submit-feedback-btn" type="submit" disabled={submittingFeedback} className="w-full rounded-full py-6 text-lg font-bold bg-primary hover:bg-primary/90">
                      {submittingFeedback ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...</>) : "Submit Feedback"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* =================== UPLOAD TAB =================== */}
          <TabsContent value="artifacts" className="mt-8">
            <div className="space-y-8">
              <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="upload-artifact-card">
                <CardHeader>
                  <CardTitle className="text-2xl text-secondary">Upload</CardTitle>
                  <CardDescription>Upload a photo or file of your child's work</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleFileUpload} className="space-y-4">
                    <div>
                      <Label htmlFor="artifact-title" className="text-base mb-2 block font-semibold">Title *</Label>
                      <Input id="artifact-title" data-testid="artifact-title-input" type="text" placeholder="Give this artefact a descriptive title" value={artifactTitle} onChange={(e) => setArtifactTitle(e.target.value)} className="h-12 rounded-xl border-2" required />
                    </div>
                    <div>
                      <Label htmlFor="file" className="text-base mb-2 block font-semibold">Choose file (photos, videos, documents)</Label>
                      <Input id="file" data-testid="artifact-file-input" type="file" accept="image/*,video/*,.pdf,.doc,.docx" onChange={(e) => setSelectedFile(e.target.files[0])} className="h-12 rounded-xl border-2" />
                      {selectedFile && <p className="mt-2 text-sm text-foreground/60">Selected: {selectedFile.name}</p>}
                    </div>
                    {isAuthenticated && (
                      <div className="p-4 bg-accent/10 rounded-xl border border-accent/30">
                        <div className="flex items-start space-x-3">
                          <Checkbox id="include-in-portfolio" data-testid="portfolio-consent-checkbox" checked={includeInPortfolio} onCheckedChange={(checked) => setIncludeInPortfolio(checked)} />
                          <div>
                            <Label htmlFor="include-in-portfolio" className="cursor-pointer font-semibold text-secondary">Include in Portfolio</Label>
                            <p className="text-sm text-foreground/60 mt-1">By checking this, you consent to include this in your child's Portfolio for showcasing their work.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <Button data-testid="upload-artifact-btn" type="submit" disabled={uploadingArtifact || !selectedFile || !artifactTitle.trim()} className="w-full rounded-full py-6 text-lg font-bold bg-primary hover:bg-primary/90">
                      {uploadingArtifact ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Uploading...</>) : (<><Upload className="mr-2 h-5 w-5" /> Upload</>)}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {artifacts.length > 0 && (
                <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="artifacts-list">
                  <CardHeader><CardTitle className="text-2xl text-secondary">Uploaded Items</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {artifacts.map((artifact) => (
                        <div key={artifact.id} data-testid={`artifact-${artifact.id}`} className="p-4 bg-muted rounded-2xl">
                          {artifact.content_type.startsWith('image/') ? (
                            <img src={`data:${artifact.content_type};base64,${artifact.file_data}`} alt={artifact.filename} className="w-full h-48 object-cover rounded-xl mb-2" />
                          ) : (
                            <div className="w-full h-48 bg-secondary/10 rounded-xl mb-2 flex items-center justify-center"><Upload className="h-12 w-12 text-secondary/40" /></div>
                          )}
                          <p className="text-sm font-semibold text-foreground truncate">{artifact.filename}</p>
                          <p className="text-xs text-foreground/60">{new Date(artifact.created_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ActivityDetail;
